import Vue from "vue";
import VueRouter from "vue-router";
import NotFound from "@/views/NotFound";
import AccountingRoutes from "./accounting";
import ConsultantsRoutes from "./consultants";
import WaitAdminApproval from "@/views/WaitAdminApproval";
import axios from "axios";
import { storeAuthData } from "@/helper/storeAuth";
import { unsubscribe } from "@/firebase/fcm";

//Globally Hide NavigationDuplicated Error
const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject);
  }
  return originalPush.call(this, location).catch((err) => {
    if (VueRouter.isNavigationFailure(err)) {
      return err;
    }
    return Promise.reject(err);
  });
};

Vue.use(VueRouter);
const isDisabled = process.env.VUE_APP_IS_ROUTE_DISABLED === "true";
const routes = [
  {
    path: "",
    component: () => import("@/layouts/Main.vue"),
    children: [
      {
        path: "/",
        redirect: "/Home",
      },
      {
        path: "/Home",
        name: "Home",
        component: () => import("@/views/HomeView.vue"),
        meta: {
          rule: "isPublic",
        },
      },
      {
        path: "/stations",
        name: "StationManagement",
        component: () => import("@/views/stations/index"),
        children: [
          {
            path: "create",
            name: "CreateStation",
            components: {
              "station-view": () => import("@/views/stations/form"),
            },
          },
          {
            path: ":id",
            name: "ShowStation",
            components: {
              "station-view": () => import("@/views/stations/show"),
            },
          },
          {
            path: ":id/update",
            name: "UpdateStation",
            components: {
              "station-view": () => import("@/views/stations/form"),
            },
          },
        ],
        meta: {
          rule: "isPublic",
        },
      },
      {
        path: "/coops",
        name: "ManagementOfCoops",
        component: () => import("@/views/coops/index.vue"),
        children: [
          {
            path: "create",
            name: "CreateCoop",
            components: {
              "coop-view": () => import("@/views/coops/form.vue"),
            },
          },
          {
            path: ":id",
            name: "ShowCoop",
            components: {
              "coop-view": () => import("@/views/coops/show.vue"),
            },
          },
          {
            path: ":id/update",
            name: "UpdateCoop",
            components: {
              "coop-view": () => import("@/views/coops/form.vue"),
            },
          },
        ],
        meta: {
          rule: "isPublic",
        },
      },
      {
        path: "/periods",
        name: "ManagementOfCourses",
        component: () => import("@/views/periods/index.vue"),
        children: [
          {
            path: "create",
            name: "CreatePeriod",
            components: {
              "period-view": () => import("@/views/periods/form"),
            },
          },
          {
            path: ":id",
            name: "ShowCourse",
            components: {
              "period-view": () => import("@/views/periods/show"),
            },
          },
          {
            path: ":id/edit",
            name: "EditPeriod",
            components: {
              "period-view": () => import("@/views/periods/form"),
            },
          },
          {
            path: ":id/treatment-plan/create",
            name: "CreateTreatmentPlan",
            components: {
              "period-view": () =>
                import("@/views/periods/treatment_plans/treatmentPlanForm"),
            },
          },
        ],
        meta: {
          rule: "isPublic",
        },
      },
      {
        path: "/employees",
        name: "ManagementOfEmployees",
        component: () => import("@/views/employees/index"),
        children: [
          {
            path: "create",
            name: "CreateEmployee",
            components: {
              "employee-view": () => import("@/views/employees/form"),
            },
          },
          {
            path: ":id",
            name: "ShowEmployee",
            components: {
              "employee-view": () => import("@/views/employees/show"),
            },
          },
          {
            path: ":id/update",
            name: "UpdateEmployee",
            components: {
              "employee-view": () => import("@/views/employees/form"),
            },
          },
        ],
        meta: {
          rule: "isPublic",
        },
      },
      {
        path: "/reports",
        name: "Reports",
        component: () => import("@/views/Reports.vue"),
        props: (route) => ({ query: route.query }),
        meta: {
          rule: "isPublic",
          isDisabled: isDisabled,
        },
      },
      {
        path: "/CompareCourses",
        name: "CompareCourses",
        component: () => import("@/views/CompareCourses.vue"),
        meta: {
          rule: "isPublic",
          isDisabled: isDisabled,
        },
      },
      ConsultantsRoutes,
      AccountingRoutes,
    ],
  },
  {
    path: "",
    component: () => import("@/layouts/FullPage.vue"),
    children: [
      {
        path: "/Login",
        name: "Login",
        component: () => import("@/views/Login.vue"),
        meta: {
          rule: "isPublic",
        },
      },
      {
        path: "/CreateAccount",
        name: "CreateAccount",
        component: () => import("@/views/CreateAccount.vue"),
        meta: {
          rule: "isPublic",
        },
      },
      {
        path: "/ForgetPassword",
        name: "ForgetPassword",
        component: () => import("@/views/ForgetPassword.vue"),
        meta: {
          rule: "isPublic",
        },
      },
      {
        path: "/OtpConfirmation",
        name: "OtpConfirmation",
        component: () => import("@/views/OtpConfirmation.vue"),
        meta: {
          rule: "isPublic",
        },
      },
      {
        path: "/newPassword",
        name: "NewPassword",
        component: () => import("@/views/NewPassword.vue"),
        meta: {
          rule: "isPublic",
        },
      },
      {
        path: "/wait-admin-approval",
        component: WaitAdminApproval,
        name: "WaitAdminApproval",
      },
    ],
  },
  {
    path: "",
    component: () => import("@/layouts/Main.vue"),
    children: [
      {
        path: "/404",
        alias: "*",
        component: NotFound,
        name: "NotFound",
      },
    ],
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  linkActiveClass: "active-link",
});

async function loadUserData(token) {
  if (localStorage.getItem("userData")) {
    await unsubscribe();
    await axios.post("logout");
    await localStorage.clear();
  }
  let { data } = (
    await axios.get(`user`, {
      headers: {
        Authorization: `Bearer ` + token,
      },
    })
  ).data;

  await storeAuthData(data, false);
}

router.beforeEach(async (to, from, next) => {
  window.scrollTo(0, 0);
  // redirect to login page if not logged in and trying to access a restricted page
  const publicPages = [
    "/login",
    "/CreateAccount",
    "/ForgetPassword",
    "/newPassword",
    "/OtpConfirmation",
    "/confirmation",
  ];
  const authRequired = !publicPages.includes(to.path);
  const token = to.query.token;
  if (authRequired && token) {
    await loadUserData(token);
    return next(to.path);
  }
  const auth = JSON.parse(localStorage.getItem("userData"));
  if (authRequired && !auth) {
    return next("/login");
  }
  if (
    auth &&
    to.name !== "WaitAdminApproval" &&
    (!auth.is_approved || !auth.is_phone_verified)
  ) {
    return next("/wait-admin-approval");
  }

  if (to.meta.isDisabled) {
    return next({ name: "NotFound" });
  }
  next();
});

export default router;
